import { resetALL } from "../../../../../.globals/redux/actions";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { clientQuery, clientMutation } from '../../../../../core/data/GraphQLApi';
import { endpoints } from '../../../../../core/defaultValues';
import { ExceptionManager } from "../../../../../core/logManager";
import { validateJsonObject } from 'Core/data/Helpers';

const route = "modules/driverPerformance/views/riskTab/redux/slice";

const INIT_STATE = {
    loadingEvents: false,
    events: [],

    updateCaseEventFlags: null,
    loadingUpdateCaseEventFlags: false,

    createDeviceReadingExtraField: null,
    loadingCreateDeviceReadingExtraField: false,

    loadingStatuses: false,
    statuses: [],
};

export const searchEvents = createAsyncThunk(
    "driverPerformance/riskTab/searchRiskCategories",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const { caseNumber } = payload;
            const response = await clientQuery(
                `
                query getCaseSummary ($caseNumber: String!) {
                    data: getCaseSummary (caseNumber: $caseNumber){
                        eventName
                        priorCoaching
                        events {
                            rowId
                            unitTime
                            deviceLabel
                            unitId
                            deviceId
                            driverName
                            eventName
                            eventCode
                            unitType
                            deviceData
                            extraFields
                        }
                    }
                }`,
                {
                    caseNumber: caseNumber
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            // Process response to group events by eventName
            const rawData = response?.data || [];

            // Group events by eventName
            const groupedEvents = {};

            rawData.forEach(category => {
                const eventName = category.eventName;
                const events = category.events || [];

                if (!groupedEvents[eventName]) {
                    groupedEvents[eventName] = {
                        id: eventName,
                        name: eventName,
                        events: []
                    };
                }

                // Filter out events with falsepositive or noissue status before adding them
                const filteredEvents = events.filter(event => {
                    if (!event.deviceData) return true;

                    try {
                        const deviceData = typeof event.deviceData === 'string'
                            ? JSON.parse(event.deviceData)
                            : event.deviceData;

                        // Get status
                        let eventStatus = deviceData.caseEventStatus;
                        if (typeof eventStatus === 'object' && eventStatus !== null) {
                            eventStatus = eventStatus.id || eventStatus.entityId || null;
                        }

                        // Filter out falsepositive and noissue
                        return eventStatus !== 'falsepositive' && eventStatus !== 'noissue';
                    } catch (e) {
                        // If parsing fails, keep the event
                        return true;
                    }
                });

                groupedEvents[eventName].events.push(...filteredEvents);
            });

            // Convert to array and add count, filter out empty categories
            const processedData = Object.values(groupedEvents)
                .map(category => ({
                    ...category,
                    count: category.events.length
                }))
                .filter(category => category.events.length > 0);

            return processedData;
        } catch (exc) {
            return rejectWithValue(exc.message);
        }
    }
);

export const updateCaseEventFlags = createAsyncThunk(
    "driverPerformance/riskTab/updateCaseEventFlags",
    async (payload, { rejectWithValue, getState }) => {
        let isReadUpdate = payload?.isReadUpdate || false;
        try {
            const response = await clientMutation(
                `
                mutation updateCaseEventFlags ($unitId: String!, $rowId: ID!, $caseEventRead: Boolean, $caseEventOmit: Boolean, $caseEventDisputed: Boolean, $caseEventStatus: String) {
                    data: updateCaseEventFlags (unitId: $unitId, rowId: $rowId, caseEventRead: $caseEventRead, caseEventOmit: $caseEventOmit, caseEventDisputed: $caseEventDisputed, caseEventStatus: $caseEventStatus)
                }`,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return { success: response.data, isReadUpdate: isReadUpdate };
        } catch (exc) {
            return { success: false, isReadUpdate: isReadUpdate, message: exc.message };
        }
    }
);

export const createDeviceReadingExtraField = createAsyncThunk(
    "driverPerformance/riskTab/createDeviceReadingExtraField",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const response = await clientMutation(
                `
                mutation createDeviceReadingExtraField ($rowId: String!, $deviceId: String!, $fieldName: String!, $jsonValue: String!, $visibleToDriver: Boolean) {
                    data: createDeviceReadingExtraField (rowId: $rowId, deviceId: $deviceId, fieldName: $fieldName, jsonValue: $jsonValue, visibleToDriver: $visibleToDriver){
                        id
                        readingId
                        fieldName
                        jsonValue
                        visibleToDriver
                        createdOn
                        createdBy
                    }
                }`,
                {
                    ...payload,
                    jsonValue: JSON.stringify(payload.jsonValue)
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response.data;
        } catch (exc) {
            return { error: true, message: exc.message }
        }
    }
);

export const searchStatuses = createAsyncThunk(
    "driverPerformance/riskTab/searchStatuses",
    async (payload, { rejectWithValue, getState }) => {
        try {

            const body = {
                conditions: [
                    {
                        fields: ["entityName"],
                        terms: ["eventStatus"],
                    },
                ],
                limit: 100,
                offset: 0,
                sort: [{ field: "label", order: "ASC" }],
            };

            const response = await clientQuery(
                `query searchEntityLists ($conditions: [EntityListSearchInput], $sort: [EntityListSortInput], $limit: Int, $offset: Int) {
                    data: searchEntityLists (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                        items {
                            entityId
                            entityName
                            parentId
                            label
                            value1
                            value2
                        }
                        total
                    }
                }`,
                body,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data?.items || [];


        } catch (exc) {
            return rejectWithValue(exc.message);
        }
    }
);

export const updateDriverPerformanceOverviewItem = createAsyncThunk(
    "driverPerformance/riskTab/updateDriverPerformanceOverviewItem",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const { unitId, rowId, status } = payload;
            
            // Return the data needed for the reducer
            return { unitId, rowId, status };
        } catch (exc) {
            ExceptionManager(exc, route, "updateDriverPerformanceOverviewItem");
            return rejectWithValue(exc);
        }
    }
);

//Slice
const driverPerformanceRiskTabRedux = createSlice({
    name: "driverPerformance/riskTab",
    initialState: INIT_STATE,
    reducers: {
        resetRiskTab(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
        updateEventDeviceDataLocally(state, action) {
            const { categoryId, eventIndex, deviceDataUpdates } = action.payload;

            // Find category
            const categoryIndex = state.events.findIndex(cat => cat.id === categoryId);
            if (categoryIndex === -1) return;

            // Get event
            const event = state.events[categoryIndex].events[eventIndex];
            if (!event) return;

            // Parse deviceData if it's a string
            let deviceData = {};
            if (event.deviceData) {
                try {
                    deviceData = typeof event.deviceData === 'string'
                        ? JSON.parse(event.deviceData)
                        : { ...event.deviceData };
                } catch (e) {
                    deviceData = {};
                }
            }

            // Update with new values (merge)
            deviceData = { ...deviceData, ...deviceDataUpdates };

            // Convert back to string and update
            state.events[categoryIndex].events[eventIndex].deviceData = JSON.stringify(deviceData);
        },
        updateEventExtraFieldsLocally(state, action) {
            const { categoryId, eventIndex, newExtraField } = action.payload;

            // Find category
            const categoryIndex = state.events.findIndex(cat => cat.id === categoryId);
            if (categoryIndex === -1) {
                return;
            }

            // Get event
            const event = state.events[categoryIndex].events[eventIndex];
            if (!event) {
                return;
            }

            // Parse extraFields if it's a string
            let extraFields = [];
            if (event.extraFields) {
                try {
                    extraFields = typeof event.extraFields === 'string'
                        ? JSON.parse(event.extraFields)
                        : [...event.extraFields];
                } catch (e) {
                    extraFields = [];
                }
            }

            // Parse jsonValue of new field if it's a string
            let processedField = { ...newExtraField };
            if (processedField.jsonValue && typeof processedField.jsonValue === 'string') {
                try {
                    processedField.jsonValue = JSON.parse(processedField.jsonValue);
                } catch (e) {
                    // If parsing fails, keep the string
                }
            }

            // Add new field
            extraFields.push(processedField);

            // Convert back to string and update
            const newExtraFieldsString = JSON.stringify(extraFields);

            state.events[categoryIndex].events[eventIndex].extraFields = newExtraFieldsString;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //searchEvents
        builder.addCase(searchEvents.pending, (state, action) => {
            state.loadingEvents = true;
        })
        builder.addCase(searchEvents.fulfilled, (state, action) => {
            state.loadingEvents = false;
            state.events = action.payload;
        })
        builder.addCase(searchEvents.rejected, (state, action) => {
            state.loadingEvents = false;
            state.error = action.payload;
        })

        //updateCaseEventFlags
        builder.addCase(updateCaseEventFlags.pending, (state, action) => {
            state.loadingUpdateCaseEventFlags = true;
        })
        builder.addCase(updateCaseEventFlags.fulfilled, (state, action) => {
            state.loadingUpdateCaseEventFlags = false;
            state.updateCaseEventFlags = action.payload;
            
            // If the update was successful and not a read-only update, handle status changes
            if (action.payload?.success && !action.payload?.isReadUpdate) {
                const { unitId, rowId, caseEventStatus } = action.meta.arg;
                
                // Normalize status
                const statusValue = caseEventStatus?.entityId || caseEventStatus?.id || caseEventStatus;
                
                // Check if we need to remove the event
                const shouldRemove = statusValue === 'falsepositive' || statusValue === 'noissue';
                
                if (shouldRemove) {
                    // Remove the event from state
                    let eventProcessed = false;
                    
                    state.events = state.events.map(category => {
                        if (eventProcessed) return category;
                        
                        const eventIndex = category.events.findIndex(
                            event => event.unitId === unitId && event.rowId === rowId
                        );
                        
                        if (eventIndex === -1) return category;
                        
                        eventProcessed = true;
                        
                        const updatedEvents = category.events.filter(
                            event => !(event.unitId === unitId && event.rowId === rowId)
                        );
                        
                        return {
                            ...category,
                            events: updatedEvents,
                            count: updatedEvents.length
                        };
                    });
                    
                    // Remove empty categories
                    state.events = state.events.filter(category => category.events.length > 0);
                }
            }
        })
        builder.addCase(updateCaseEventFlags.rejected, (state, action) => {
            state.loadingUpdateCaseEventFlags = false;
            state.error = action.payload;
        })

        //createDeviceReadingExtraField
        builder.addCase(createDeviceReadingExtraField.pending, (state, action) => {
            state.loadingCreateDeviceReadingExtraField = true;
        })
        builder.addCase(createDeviceReadingExtraField.fulfilled, (state, action) => {
            state.loadingCreateDeviceReadingExtraField = false;
            state.createDeviceReadingExtraField = action.payload;
        })
        builder.addCase(createDeviceReadingExtraField.rejected, (state, action) => {
            state.loadingCreateDeviceReadingExtraField = false;
            state.error = action.payload;
        })

        //searchStatuses
        builder.addCase(searchStatuses.pending, (state, action) => {
            state.loadingStatuses = true;
        })
        builder.addCase(searchStatuses.fulfilled, (state, action) => {
            state.loadingStatuses = false;
            state.statuses = action.payload;
        })
        builder.addCase(searchStatuses.rejected, (state, action) => {
            state.loadingStatuses = false;
            state.error = action.payload;
        })

        // updateDriverPerformanceOverviewItem
        builder.addCase(updateDriverPerformanceOverviewItem.fulfilled, (state, action) => {
            const { unitId, rowId, status } = action.payload;
            
            // Get the status value
            const statusValue = status?.entityId || status?.id || status;
            
            // Check if we need to remove the event
            const shouldRemove = statusValue === 'falsepositive' || statusValue === 'noissue';
            
            // Flag to track if event was found and processed
            let eventProcessed = false;
            
            // Iterate through all categories to find and update the event
            state.events = state.events.map(category => {
                // If event already processed, skip
                if (eventProcessed) {
                    return category;
                }
                
                // Find the event index in this category
                const eventIndex = category.events.findIndex(
                    event => event.unitId === unitId && event.rowId === rowId
                );
                
                // If event not found in this category, return category unchanged
                if (eventIndex === -1) {
                    return category;
                }
                
                // Mark as processed
                eventProcessed = true;
                
                // If we need to remove the event
                if (shouldRemove) {
                    const updatedEvents = category.events.filter(
                        event => !(event.unitId === unitId && event.rowId === rowId)
                    );
                    
                    return {
                        ...category,
                        events: updatedEvents,
                        count: updatedEvents.length
                    };
                }
                
                // Otherwise, update the event
                const event = category.events[eventIndex];
                
                // Parse deviceData (single stringified in riskTab)
                let deviceData = validateJsonObject(event.deviceData);
                
                // Update caseEventStatus with new status
                deviceData.caseEventStatus = statusValue;
                
                // Convert back to original format (single stringified)
                const updatedDeviceData = JSON.stringify(deviceData);
                
                // Update the event in the category
                const updatedEvents = [...category.events];
                updatedEvents[eventIndex] = {
                    ...event,
                    deviceData: updatedDeviceData
                };
                
                return {
                    ...category,
                    events: updatedEvents,
                    count: updatedEvents.length
                };
            });
            
            // Remove categories with no events
            state.events = state.events.filter(category => category.events.length > 0);
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = driverPerformanceRiskTabRedux;

// Extract and export each action creator by name
export const { resetRiskTab, updateEventDeviceDataLocally, updateEventExtraFieldsLocally } = actions;

// Export the reducer, either as a default or named export
export default reducer;
